import { getSettings } from '../../queries.js';
import { nextLocalPrescriptionCode } from '../patient.mutators.js';
import { zql } from '../../schema.js';
import { inUseGfrQuery } from '../../util/patient-gfr.js';
import { uuidv7 } from 'uuidv7';
import { addDaysUtc, formatMinutesToTime, parseTimeToMinutes } from '../../util/functions.js';
import { getDosingWeight } from '../../util/calculations/dosing-weight.js';
import { getDrugRoundingMode, getTotalAmountOfSubstanceDecimal } from '../../util/calculations/patient-drug-total-amount.js';
import { isoDateFromTimestamp } from '../../util/protocol-cycles.js';
import { EventLogResources, addEventLog } from '../util.js';
import { EventLogs, getTherapySequenceInitialStatus } from '../../utils.js';
import { createPatch } from 'rfc6902';
import { logAmmsMfnMessage } from './add-therapy-parameter.js';
import { getDoseBasis } from './dose-basis.js';
function loadProtocolSequences(tx, protocolId, removedSequences) {
    return tx.run(zql.protocolTherapySequence
        .related('medicine', medicine => medicine
        .related('activeSubstance', drug => drug.related('activeSubstanceDetails').related('supportiveMedicationDetails'))
        .related('prescribedName', pn => pn.related('drugCatalogItem', item => item.related('activeSubstanceDetails'))))
        .where('protocolId', '=', protocolId)
        .where('id', 'NOT IN', removedSequences ?? [])
        .where('deletedAt', 'IS', null));
}
function loadCharacteristics(tx, patientId) {
    return tx.run(zql.patientCharacteristics
        .related('patient')
        .where('patientId', '=', patientId)
        .orderBy('createdAt', 'desc')
        .limit(1)
        .one());
}
function loadGfr(tx, patientId) {
    return tx.run(inUseGfrQuery(patientId));
}
/**
 * Builds (without writing) the patientTherapySequence + medicine rows for the given cycles,
 * from the protocol's template sequences. `cycles` carries each cycle's own absolute start
 * date so callers can generate cycles that aren't evenly spaced from a single anchor date
 * (e.g. a single cycle added later with a custom date).
 */
export function buildCycleTherapySequences(params) {
    const { patientId, protocolId, patientProtocolId, protocolSequences, characteristics, gfr, isZeroDayProtocol, startTime, cycles, userId, now, responsibleDoctorId, } = params;
    // The doctor sets what time the earliest therapy of the protocol should start; every
    // other therapy's time shifts by the same number of minutes, potentially rolling over
    // into the next day (e.g. template times 00:00/02:00 with a 13:00 start become 13:00/15:00).
    const earliestTemplateMinutes = protocolSequences.length
        ? Math.min(...protocolSequences.map(seq => parseTimeToMinutes(seq.time)))
        : 0;
    const startTimeDeltaMinutes = parseTimeToMinutes(startTime) - earliestTemplateMinutes;
    const therapySequences = [];
    const medicineRows = [];
    const sequenceMedicines = new Map();
    for (const seq of protocolSequences) {
        const sequenceGroupId = uuidv7();
        const shiftedMinutes = parseTimeToMinutes(seq.time) + startTimeDeltaMinutes;
        const timeDayOffset = Math.floor(shiftedMinutes / 1440);
        const shiftedTime = formatMinutesToTime(shiftedMinutes);
        for (const { cycle, startDate } of cycles) {
            const day = isZeroDayProtocol ? seq.day : seq.day - 1;
            const therapyDate = addDaysUtc(startDate, day + timeDayOffset);
            const newTherapySequenceId = uuidv7();
            const requiresAdditionalApproval = Boolean(seq.medicine?.some(m => m.activeSubstance?.activeSubstanceDetails?.additionalApprovalRequired ||
                m.prescribedName?.drugCatalogItem?.activeSubstanceDetails?.additionalApprovalRequired));
            const newPatientTherapySequence = {
                id: newTherapySequenceId,
                patientId,
                protocolId,
                sequenceGroupId,
                patientProtocolId,
                therapyDate,
                prescriptionSource: 'cypro',
                restVialCreated: false,
                status: getTherapySequenceInitialStatus(requiresAdditionalApproval),
                cycle,
                isNote: seq.isNote ?? false,
                note: seq.note ?? null,
                day: seq.day,
                time: shiftedTime,
                carrierSolutionId: seq.carrierSolutionId ?? undefined,
                applicationFormId: seq.applicationFormId ?? undefined,
                duration: seq.duration ?? undefined,
                volume: seq.volume ?? undefined,
                volumeMode: seq.volumeMode ?? undefined,
                createdAt: now,
                createdBy: userId,
                therapyNote: seq.therapyNote,
                pharmacyNote: seq.pharmacyNote,
                responsibleDoctorId: responsibleDoctorId ?? undefined,
                ...getDoseBasis(characteristics, gfr, { at: now }),
            };
            therapySequences.push(newPatientTherapySequence);
            const medicinesForHl7 = [];
            for (const med of seq.medicine) {
                const totalAmount = getTotalAmountOfSubstanceDecimal({
                    calculationMethod: med.calculationMethod ?? 'ABSOLUTE',
                    dose: med.regularDose,
                    doseProportion: 100,
                    roundingMode: getDrugRoundingMode(med.activeSubstance),
                    dosePrecision: med.precisionDecimals ?? 2,
                    height: characteristics?.height ?? null,
                    weight: getDosingWeight(characteristics),
                    gfr: gfr?.gfr ?? null,
                    bsa: characteristics?.bsa ?? null,
                });
                const medicineId = uuidv7();
                medicineRows.push({
                    id: medicineId,
                    therapySequenceId: newTherapySequenceId,
                    patientId,
                    therapyDate,
                    activeSubstanceId: med.activeSubstanceId ?? undefined,
                    prescribedNameId: med.prescribedNameId ?? undefined,
                    autIdem: med.autIdem ?? false,
                    regularDose: med.regularDose,
                    recommendedDose: med.regularDose,
                    totalAmount,
                    precisionDecimals: med.precisionDecimals,
                    calculationMethod: med.calculationMethod ?? undefined,
                    notes: med.notes ?? null,
                    ageRanges: med.ageRanges ?? undefined,
                    createdAt: now,
                    createdBy: userId,
                });
                medicinesForHl7.push({
                    id: medicineId,
                    activeSubstanceId: med.activeSubstanceId ?? undefined,
                    prescribedNameId: med.prescribedNameId ?? undefined,
                    totalAmount,
                });
            }
            sequenceMedicines.set(newTherapySequenceId, {
                applicationFormId: seq.applicationFormId ?? undefined,
                carrierSolutionId: seq.carrierSolutionId ?? undefined,
                volume: seq.volume ?? undefined,
                medicines: medicinesForHl7,
            });
        }
    }
    return { therapySequences, medicineRows, sequenceMedicines };
}
export async function insertCycleTherapySequences(tx, build) {
    const ops = [
        ...build.therapySequences.map(seq => () => tx.mutate.patientTherapySequence.insert(seq)),
        ...build.medicineRows.map(row => () => tx.mutate.patientTherapySequenceMedicine.insert(row)),
    ];
    await Promise.all(ops.map(op => op()));
}
/**
 * Assigns local prescription codes and, on the server, writes the creation event log and
 * AMMS/HL7 messages for newly-inserted therapy sequences. Must run after
 * `insertCycleTherapySequences` has written the rows.
 */
export async function finalizeCycleTherapySequences(tx, params) {
    const { therapySequences, sequenceMedicines, characteristics, userId, now } = params;
    const logSequenceOps = [];
    const groupedSequences = Object.groupBy(therapySequences, seq => seq.therapyDate);
    await Promise.all(Object.entries(groupedSequences).map(async ([_date, sequences]) => {
        if (sequences && sequences[0]) {
            const prescriptionDate = new Date(sequences[0].therapyDate);
            const internalIdStructure = await nextLocalPrescriptionCode(tx, `PR-${prescriptionDate.getFullYear()}${String(prescriptionDate.getMonth() + 1).padStart(2, '0')}${String(prescriptionDate.getDate()).padStart(2, '0')}`);
            await Promise.all(sequences.map(async (sequence, index) => {
                const internalId = `${internalIdStructure.prefixDash}${internalIdStructure.maxNum + index + 1}`;
                await tx.mutate.patientTherapySequence.update({
                    id: sequence.id,
                    internalId,
                });
                if (tx.location === 'server') {
                    const patches = createPatch(sequence, { ...sequence, internalId });
                    logSequenceOps.push(() => addEventLog(tx, {
                        id: uuidv7(),
                        resource: EventLogResources.patientTherapySequence,
                        resourceId: sequence.id,
                        action: 'create',
                        domain: 'prescription',
                        event: EventLogs.PATIENT_THERAPY_SEQUENCE_ADDED,
                        patches,
                        performedBy: userId,
                        performedAt: now,
                        details: {
                            patientId: sequence.patientId,
                            patientName: characteristics?.patient?.fullName ?? '',
                            id: internalId,
                        },
                    }));
                    const sequenceMedicine = sequenceMedicines.get(sequence.id);
                    if (sequenceMedicine) {
                        logSequenceOps.push(() => logAmmsMfnMessage(tx, {
                            sequence: {
                                id: sequence.id,
                                internalId,
                                applicationFormId: sequenceMedicine.applicationFormId,
                                carrierSolutionId: sequenceMedicine.carrierSolutionId,
                                volume: sequenceMedicine.volume,
                            },
                            medicines: sequenceMedicine.medicines,
                            change: 'add',
                            now,
                        }));
                    }
                }
            }));
        }
    }));
    await Promise.all(logSequenceOps.map(op => op()));
}
export async function assignPatientProtocol(tx, input, userId) {
    const now = Date.now();
    const patientProtocolId = input.patientProtocolId;
    const protocol = await tx.run(zql.protocols.where('id', '=', input.protocolId).where('deletedAt', 'IS', null).one());
    if (!protocol)
        throw new Error('Not Found');
    const cycleStartDates = {};
    const cyclesToGenerate = [];
    for (let cycleIndex = 0; cycleIndex < input.cycles; cycleIndex++) {
        const cycleDate = addDaysUtc(input.startDate, cycleIndex * protocol.daysRepetition);
        cycleStartDates[cycleIndex + 1] = isoDateFromTimestamp(cycleDate);
        cyclesToGenerate.push({ cycle: cycleIndex + 1, startDate: cycleDate });
    }
    await tx.mutate.patientProtocols.insert({
        id: patientProtocolId,
        patientId: input.patientId,
        protocolId: input.protocolId,
        startDate: input.startDate,
        cycles: input.cycles,
        cycleStartDates,
        createdAt: now,
        createdBy: userId,
    });
    const protocolSequences = await loadProtocolSequences(tx, input.protocolId, input.removedSequences);
    const characteristics = await loadCharacteristics(tx, input.patientId);
    const gfr = await loadGfr(tx, input.patientId);
    const settings = await getSettings(tx);
    const isZeroDayProtocol = settings?.dayZeroProtocol;
    const build = buildCycleTherapySequences({
        patientId: input.patientId,
        protocolId: input.protocolId,
        patientProtocolId,
        protocolSequences,
        characteristics,
        gfr,
        isZeroDayProtocol,
        startTime: input.startTime,
        cycles: cyclesToGenerate,
        userId,
        now,
        responsibleDoctorId: input.responsibleDoctorId,
    });
    await insertCycleTherapySequences(tx, build);
    await finalizeCycleTherapySequences(tx, {
        therapySequences: build.therapySequences,
        sequenceMedicines: build.sequenceMedicines,
        characteristics,
        userId,
        now,
    });
}
//# sourceMappingURL=assign-patient-protocol.js.map